/**
 * CursorAura — a soft ring that trails the pointer and swells into a green
 * halo over anything you can click (web, fine pointers only). The system
 * cursor stays; the ring is decoration on top, never in the way: no pointer
 * events, hidden over text fields, gone when the pointer leaves the window.
 * One rAF loop that stops as soon as the ring catches up. Off under reduced
 * motion, on touch and on native. Mount once per page:
 *
 *   <CursorAura />
 */
import { useEffect } from 'react';
import { IS_WEB, useReducedMotion } from '../../utils/motion';
import injectCss from '../../utils/injectCss';

const CSS = `
[data-cursor-aura] { position: fixed; left: 0; top: 0; width: 34px; height: 34px; margin: -17px 0 0 -17px; border-radius: 50%; pointer-events: none; z-index: 2147483000;
  border: 1px solid rgba(255,255,255,0.4); background: transparent; opacity: 0; will-change: transform;
  transition: width 360ms cubic-bezier(0.16,1,0.3,1), height 360ms cubic-bezier(0.16,1,0.3,1), margin 360ms cubic-bezier(0.16,1,0.3,1), background-color 260ms, border-color 260ms, opacity 220ms; }
[data-cursor-aura="on"] { opacity: 1; }
[data-cursor-aura="hover"] { opacity: 1; width: 66px; height: 66px; margin: -33px 0 0 -33px; border-color: rgba(52,211,153,0.7); background-color: rgba(52,211,153,0.10); }
[data-cursor-aura="card"] { opacity: 1; width: 14px; height: 14px; margin: -7px 0 0 -7px; border-color: transparent; background-color: rgba(52,211,153,0.9); }
@media (prefers-reduced-motion: reduce) { [data-cursor-aura] { display: none; } }
`;

const CLICKABLE = '[data-cta],[data-pressable],a[href],button,[role="button"],[role="link"],[role="switch"],[role="tab"]';
const CARD = '[data-spotlight]';
const TEXT = 'input,textarea,select,[contenteditable="true"]';
const EASE = 0.2;

export default function CursorAura() {
    const reduced = useReducedMotion();

    useEffect(() => {
        if (!IS_WEB || reduced || typeof window === 'undefined' || typeof document === 'undefined') return undefined;
        if (!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return undefined;
        injectCss('cursor-aura', CSS);
        const el = document.createElement('div');
        el.setAttribute('data-cursor-aura', 'off');
        el.setAttribute('aria-hidden', 'true');
        document.body.appendChild(el);

        let x = 0; let y = 0; let tx = 0; let ty = 0; let raf = 0; let seen = false;
        const paint = () => { el.style.transform = `translate3d(${x}px, ${y}px, 0)`; };
        const tick = () => {
            x += (tx - x) * EASE;
            y += (ty - y) * EASE;
            paint();
            raf = Math.abs(tx - x) + Math.abs(ty - y) > 0.2 ? requestAnimationFrame(tick) : 0;
        };
        const onMove = (e) => {
            if (e.pointerType && e.pointerType !== 'mouse') return;
            tx = e.clientX; ty = e.clientY;
            if (!seen) { seen = true; x = tx; y = ty; paint(); }
            const t = e.target instanceof Element ? e.target : null;
            const state = !t ? 'on' : t.closest(TEXT) ? 'off' : t.closest(CLICKABLE) ? 'hover' : t.closest(CARD) ? 'card' : 'on';
            if (el.getAttribute('data-cursor-aura') !== state) el.setAttribute('data-cursor-aura', state);
            if (!raf) raf = requestAnimationFrame(tick);
        };
        const onLeave = () => el.setAttribute('data-cursor-aura', 'off');
        window.addEventListener('pointermove', onMove, { passive: true });
        document.documentElement.addEventListener('pointerleave', onLeave);
        return () => {
            if (raf) cancelAnimationFrame(raf);
            window.removeEventListener('pointermove', onMove);
            document.documentElement.removeEventListener('pointerleave', onLeave);
            el.remove();
        };
    }, [reduced]);

    return null;
}
